import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  CAMPS_EDITABLES,
  usePrevisualitzaSyncUsuari,
  useSyncUsuari,
  type CampEditable,
  type EdicioUsuariDto,
  type UsuariPerfilDto,
} from '@/api/ajustosPersones';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { BarraCanvis } from '../marc/canvis';
import { Confirmacio } from '../marc/Calaix';
import { opcionsDepartament, useEstructura } from '../marc/estructura';
import { ErrorDesa } from '../comu';

type Valors = Record<CampEditable, string>;

const valorsDe = (u: UsuariPerfilDto): Valors => ({
  nom: u.nom ?? '',
  cognoms: u.cognoms ?? '',
  carrec: u.carrec ?? '',
  departamentId: u.departamentId != null ? String(u.departamentId) : '',
  telefon: u.telefon ?? '',
  idioma: u.idioma ?? '',
});

const aEdicio = (v: Valors, actiu: boolean): EdicioUsuariDto => ({
  nom: v.nom.trim(),
  cognoms: v.cognoms.trim() || null,
  carrec: v.carrec.trim() || null,
  departamentId: v.departamentId ? Number(v.departamentId) : null,
  telefon: v.telefon.trim() || null,
  idioma: v.idioma || null,
  actiu,
});

/**
 * Pestanya Perfil: dades de la persona editables, amb l'origen de cada dada (Entra ID o
 * modificada al CAI), el valor d'Entra per a les modificades i l'estat actiu/inactiu.
 */
export function PerfilUsuari({
  usuari,
  editable,
  desa,
  desant,
  errorDesa,
}: {
  usuari: UsuariPerfilDto;
  editable: boolean;
  desa: (cos: EdicioUsuariDto) => void;
  desant: boolean;
  errorDesa: unknown;
}) {
  const { t } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const estructura = useEstructura();
  const base = useMemo(() => valorsDe(usuari), [usuari]);
  const [v, setV] = useState<Valors>(base);
  const [confirmaEstat, setConfirmaEstat] = useState(false);
  const locals = new Set(usuari.campsLocals ?? []);
  const previa = usePrevisualitzaSyncUsuari(usuari.id, locals.size > 0);
  const recupera = useSyncUsuari(usuari.id);
  const entra = new Map((previa.data?.diferencies ?? []).map((d) => [d.camp, d.valorEntra]));
  const canviats = CAMPS_EDITABLES.filter((c) => v[c] !== base[c]);
  const nom = `${usuari.nom} ${usuari.cognoms ?? ''}`.trim();

  const origen = (c: CampEditable | 'email' | 'upn') => {
    if (c === 'email' || c === 'upn')
      return (
        <span className="badge badge--gray" title={t('persones.perfil.bloquejatTitol')}>
          <Icona nom="lock" />
          {t('persones.perfil.origen.bloquejat')}
        </span>
      );
    if (v[c] !== base[c])
      return <span className="badge badge--preventiu">{t('persones.perfil.origen.pendent')}</span>;
    // L'idioma no ve d'Entra: el tria la persona a les preferències de l'app.
    if (c === 'idioma') return null;
    if (locals.has(c))
      return <span className="badge badge--incident">{t('persones.perfil.origen.cai')}</span>;
    return <span className="badge badge--gray">{t('persones.perfil.origen.entra')}</span>;
  };

  const cap = (c: CampEditable | 'email' | 'upn') => (
    <span className="camp__cap">
      <label className="camp__label" htmlFor={`perfil-${c}`}>
        {t(`persones.perfil.camps.${c}`)}
      </label>
      {origen(c)}
    </span>
  );

  const ajudaEntra = (c: CampEditable) =>
    locals.has(c) && v[c] === base[c] && entra.has(c) ? (
      <span className="camp__ajuda" data-entra={c}>
        {t('persones.perfil.aEntra')}{' '}
        <strong>{entra.get(c) || t('persones.perfil.senseValor')}</strong>
        {editable ? (
          <>
            {' · '}
            <button
              type="button"
              className="enllac-boto"
              disabled={recupera.isPending}
              onClick={() => recupera.mutate([c])}
            >
              {t('persones.perfil.recupera')}
            </button>
          </>
        ) : null}
      </span>
    ) : null;

  const text = (c: CampEditable) => (
    <div className="camp" key={c}>
      {cap(c)}
      <input
        id={`perfil-${c}`}
        className="input"
        value={v[c]}
        disabled={!editable}
        aria-invalid={c === 'nom' && !v.nom.trim() ? true : undefined}
        onChange={(e) => setV({ ...v, [c]: e.target.value })}
      />
      {ajudaEntra(c)}
    </div>
  );

  // Elements dels camps, fora del JSX: les claus de camp no són text visible.
  const el = {
    nom: text('nom'),
    cognoms: text('cognoms'),
    carrec: text('carrec'),
    telefon: text('telefon'),
    capEmail: cap('email'),
    capUpn: cap('upn'),
    capDep: cap('departamentId'),
    capIdioma: cap('idioma'),
    entraDep: ajudaEntra('departamentId'),
    entraIdioma: ajudaEntra('idioma'),
  };

  return (
    <>
      {locals.size > 0 ? (
        <div className="info-banda">
          <Icona nom="pencil" />
          <span>
            <Trans
              t={t}
              i18nKey="persones.perfil.avisLocals"
              count={locals.size}
              components={{ b: <strong /> }}
            />
          </span>
        </div>
      ) : (
        <div className="info-banda">
          <Icona nom="checkmark-circle" />
          {t('persones.perfil.totesEntra')}
        </div>
      )}
      <div className="panel">
        <div className="panel__head">
          <h3>{t('persones.perfil.dades')}</h3>
        </div>
        <div className="panel__body form">
          <div className="perfil-graella">
            {el.nom}
            {el.cognoms}
            <div className="camp">
              {el.capEmail}
              <input id="perfil-email" className="input" value={usuari.email ?? ''} disabled />
            </div>
            <div className="camp">
              {el.capUpn}
              <input id="perfil-upn" className="input mono" value={usuari.upn ?? ''} disabled />
            </div>
            {el.carrec}
            <div className="camp">
              {el.capDep}
              <Desplegable
                id="perfil-departamentId"
                aria-label={t('persones.perfil.camps.departamentId')}
                valor={v.departamentId}
                disabled={!editable}
                opcions={[
                  { valor: '', etiqueta: '—' },
                  ...opcionsDepartament(estructura, {
                    seleccionat: usuari.departamentId,
                  }).map((o) => ({ ...o, valor: String(o.valor) })),
                ]}
                onCanvia={(x) => setV({ ...v, departamentId: String(x) })}
              />
              {el.entraDep}
            </div>
            {el.telefon}
            <div className="camp">
              {el.capIdioma}
              <Desplegable
                id="perfil-idioma"
                aria-label={t('persones.perfil.camps.idioma')}
                valor={v.idioma}
                disabled={!editable}
                opcions={[
                  { valor: '', etiqueta: '—' },
                  { valor: 'ca', etiqueta: tc('idioma.ca') },
                  { valor: 'es', etiqueta: tc('idioma.es') },
                ]}
                onCanvia={(x) => setV({ ...v, idioma: String(x) })}
              />
              {el.entraIdioma}
            </div>
          </div>
          <ErrorDesa error={recupera.error} />
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <h3>{t('persones.perfil.estat')}</h3>
        </div>
        <div className="panel__body perfil-estat">
          <span className={`badge badge--${usuari.actiu ? 'ok' : 'gray'}`}>
            {usuari.actiu ? t('persones.perfil.actiu') : t('persones.perfil.inactiu')}
          </span>
          <span className="t-footnote dim">{t('persones.perfil.ajudaEstat')}</span>
          <span className="spacer" />
          {editable ? (
            <button
              type="button"
              className={`btn btn--s ${usuari.actiu ? 'btn--danger' : 'btn--outline'}`}
              onClick={() => setConfirmaEstat(true)}
            >
              {usuari.actiu ? t('persones.perfil.desactiva') : t('persones.perfil.reactiva')}
            </button>
          ) : null}
        </div>
      </div>

      <ErrorDesa error={errorDesa} />

      {editable ? (
        <BarraCanvis
          n={canviats.length}
          nom={nom}
          onDescarta={() => setV(base)}
          onDesa={() => desa(aEdicio(v, usuari.actiu))}
          desant={desant}
          desactivat={!v.nom.trim()}
        >
          {!v.nom.trim() ? t('persones.perfil.nomObligatori') : null}
        </BarraCanvis>
      ) : null}

      {confirmaEstat ? (
        <Confirmacio
          titol={t(
            usuari.actiu ? 'persones.perfil.desactivaTitol' : 'persones.perfil.reactivaTitol',
            {
              nom,
            },
          )}
          textAccio={usuari.actiu ? t('persones.perfil.desactiva') : t('persones.perfil.reactiva')}
          perill={usuari.actiu}
          ocupat={desant}
          onTanca={() => setConfirmaEstat(false)}
          onConfirma={() => {
            desa(aEdicio(base, !usuari.actiu));
            setConfirmaEstat(false);
          }}
        >
          <p>
            {usuari.actiu ? t('persones.perfil.desactivaText') : t('persones.perfil.reactivaText')}
          </p>
        </Confirmacio>
      ) : null}
    </>
  );
}
